// The per-partner "started and never finished" follow-up list, for the staff
// Prequalification tab on the Broker and Brokerage detail pages.
//
// A PANEL, not a section: the tab owns the heading (the sub-tab label reads
// "In progress") and mounts one panel at a time. The Abandoned / All drafts
// toggle stays HERE — it filters this one list, so it is a control on the
// panel rather than a fourth tab.
//
// A SIBLING of the cross-book DraftsSection on the admin Prequalification list
// page, deliberately — not a replacement for it and not a component shared with
// it. The two answer different questions: that queue is the liaison's whole
// to-do list, this one answers "what is stalled on THIS partner's site". Both
// read the same parallel drafts source (a draft is neither a submission nor an
// invite, so no filter on either of those lists could ever return one).
//
// STAFF ONLY. Nothing here may be reached from the portal — see the comment on
// the read below, and the boundary assertion in the co-located test.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileClock, PenLine } from "lucide-react";
import { Badge, Button, EmptyState, SearchField, SortSelect } from "@/design-system/components/ui";
import { PREQUAL_INVITE_STATUS_BADGE } from "@/design-system/lib/prequalInviteStatus";
import { cn } from "@/design-system/lib/utils";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Pagination } from "@/components/layout/Pagination";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import { usePrequalificationDrafts } from "@/hooks/prequalification/usePrequalification";
import type {
  PrequalificationDraftScope,
  PrequalificationDraftSummaryDto,
  PrequalSort,
} from "@/api/prequalification";

/** 25, matching the completions and invitations panels beside it. It was 10
 *  while all three lists stacked on one scrolling page under a site card and a
 *  completions table; behind a tab this list is the whole panel. */
const PAGE_SIZE = 25;

/** Keystrokes are cheap; a paged server query is not. */
const SEARCH_DEBOUNCE_MS = 300;

/** Display labels only — the two WIRE tokens are single-sourced as
 *  `PrequalSort`. Here "newest" orders on `lastActivityAtUtc`: the most recent
 *  thing that happened on the row, which is what a follow-up queue wants. */
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

export interface PrequalSiteDraftsSectionProps {
  /** Which record this tab is mounted on. Copy only — see `ownerId`. */
  ownerKind: "broker" | "brokerage" | "buyerTeam";
  /**
   * The record's own id. Deliberately NOT used to scope the read, and that is
   * the point of this whole panel: a draft carries a partner BRAND, never a
   * broker, so there is no per-broker attribution to filter on and none may be
   * invented. Part of the contract so both call sites pass the same shape.
   */
  ownerId: string;
  /**
   * The site this tab RESOLVED — which for a broker who inherits is their
   * FIRM's brand, not theirs. `null` means the owner has no site at all.
   */
  brandId: string | null;
  /**
   * REQUIRED and nullable rather than optional, so mounting this without
   * deciding is a compile error rather than a silent omission.
   *
   * For an INHERITED site the resolved `brandId` belongs to the covering firm,
   * so this list is the WHOLE FIRM's drafts — pass the caption that says so, the
   * same way the completions panel on this tab already does. Read as "drafts
   * this broker started", it is simply wrong. `null` only when the site is the
   * owner's own.
   */
  caption: string | null;
}

/**
 * Split in two on purpose: the brand is resolved BEFORE any hook runs, so a
 * partner with no site cannot issue an unscoped read. An `undefined`
 * `partnerBrandId` on this query means "every site in the book", which is the
 * one thing this panel must never render.
 */
export function PrequalSiteDraftsSection(props: PrequalSiteDraftsSectionProps) {
  const { ownerKind, brandId, caption } = props;

  if (brandId === null) {
    return (
      <section data-testid="prequal-site-drafts" aria-label="In-progress applications">
        <EmptyState
          icon={FileClock}
          title="No in-progress applications"
          description={
            ownerKind === "broker"
              ? "This broker has no prequalification site, so no applications can be started against one."
              : ownerKind === "brokerage"
                ? "This firm has no prequalification site, so no applications can be started against one."
                : "This team has no prequalification site, so no applications can be started against one."
          }
        />
      </section>
    );
  }

  return <BrandScopedDrafts brandId={brandId} caption={caption} />;
}

function BrandScopedDrafts({
  brandId,
  caption,
}: {
  brandId: string;
  caption: string | null;
}) {
  const { date, relative } = useDateFormatter();
  const { hasPermission } = useAuth();
  // Same gate as the cross-book queue: only a caller who may actually drive the
  // form is offered the action, so nobody is handed a link that 403s.
  const canAssist = hasPermission("Prequalification.Manage");

  const [scope, setScope] = useState<PrequalificationDraftScope>("Abandoned");
  const [page, setPage] = useState(1);
  // `rawSearch` is what the box shows; `q` is what the server has been asked.
  const [rawSearch, setRawSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<PrequalSort>("newest");

  useEffect(() => {
    setPage(1);
  }, [scope]);

  useEffect(() => {
    // Nothing to debounce — including on mount, where this effect otherwise
    // scheduled an unconditional setPage(1) that landed ~300ms in and silently
    // reverted a Next pressed inside that window.
    const trimmed = rawSearch.trim();
    if (trimmed === q) return;
    const timer = window.setTimeout(() => {
      setQ(trimmed);
      // A new search is a new result set; staying on page 4 of the old one
      // would show an empty page and read as "no matches".
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [rawSearch, q]);

  // 🔴 STAFF-ONLY READ. `partnerBrandId` narrows to one site, and an ORGANIC
  // draft — somebody who wandered onto the public funnel and was never invited
  // by anyone — has BOTH actor columns null but still carries the brand, so it
  // is returned here. On this ADMIN tab that is correct and intended: staff see
  // everything. In a PARTNER-FACING query it would hand a firm the partial
  // financial applications of strangers. Do not copy this call as the model for
  // a portal read; the portal has its own entitlement-scoped path.
  const drafts = usePrequalificationDrafts({
    page,
    pageSize: PAGE_SIZE,
    scope,
    partnerBrandId: brandId,
    // Blank is dropped by the client's query builder either way; sending
    // undefined keeps an empty box indistinguishable from an unsent param.
    q: q || undefined,
    sort,
  });

  const totalPages = useMemo(() => {
    const data = drafts.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [drafts.data]);

  const columns: Column<PrequalificationDraftSummaryDto>[] = useMemo(
    () => [
      {
        key: "applicant",
        header: "Applicant",
        render: (d) => (
          <div>
            <div className="font-semibold">
              {d.firstName} {d.lastName}
            </div>
            <div className="text-caption text-gray-mid">{d.email}</div>
          </div>
        ),
      },
      {
        key: "role",
        header: "Role",
        render: (d) => <Badge status={d.role === "Buyer" ? "info" : "gold"}>{d.role}</Badge>,
      },
      {
        key: "progress",
        header: "Progress",
        // `stepIndex` is a ZERO-BASED position, so rendering it raw told a
        // partner that somebody sitting on their first screen had done "0 of 6
        // steps" — and it is the column this section exists for, on a queue of
        // people worth phoning. +1 turns the position into a human count; the
        // `Math.min` holds the ceiling for a row stored past the end of its own
        // funnel (see `ClampStep` — seller rows written under the old bound are
        // not normalised by any migration). Same expression as the portal's
        // `DraftsPanel.stepLabel`, which had it right first.
        render: (d) => (
          <span className="text-caption">
            {Math.min(d.stepIndex + 1, d.stepCount)} of {d.stepCount} steps
          </span>
        ),
      },
      {
        key: "source",
        header: "Source",
        // An organic draft has no invite and therefore no invite status. It must
        // never fall through to the badge map: saying this partner invited
        // somebody who arrived on their public link unprompted is actively
        // wrong, and on a brand-scoped list these rows are common.
        render: (d) =>
          d.inviteStatus ? (
            <Badge status={PREQUAL_INVITE_STATUS_BADGE[d.inviteStatus]}>{d.inviteStatus}</Badge>
          ) : (
            <span className="text-caption text-gray-mid italic">Not invited</span>
          ),
      },
      {
        key: "activity",
        header: "Last activity",
        render: (d) => (
          <div className="text-caption">
            {relative(d.lastActivityAtUtc)}
            <div className="text-gray-mid">Started {date(d.createdAt)}</div>
          </div>
        ),
      },
      {
        key: "state",
        header: "State",
        render: (d) =>
          d.isAbandoned ? (
            <Badge status="review">Abandoned</Badge>
          ) : (
            <Badge status="draft">In progress</Badge>
          ),
      },
      ...(canAssist
        ? [
            {
              key: "assist",
              header: "",
              render: (d: PrequalificationDraftSummaryDto) => (
                // The accessible name sits on the LINK, not the button inside
                // it: that is what a keyboard or screen-reader user activates,
                // and one row's "Continue" is otherwise indistinguishable from
                // the next.
                <Link
                  to={`/deal-flow/prequalify/assist/${d.draftId}`}
                  aria-label={`Continue ${d.firstName} ${d.lastName}'s application`}
                >
                  <Button variant="tertiary" size="sm" tabIndex={-1}>
                    <PenLine size={14} strokeWidth={1.5} aria-hidden className="mr-space-2" />
                    Continue
                  </Button>
                </Link>
              ),
            },
          ]
        : []),
    ],
    [date, relative, canAssist],
  );

  return (
    <section data-testid="prequal-site-drafts" aria-label="In-progress applications">
      {caption && (
        <p
          className="mb-space-2 mt-0 text-caption text-gray-mid"
          data-testid="prequal-site-drafts-caption"
        >
          {caption}
        </p>
      )}

      <div className="mb-space-3 flex flex-wrap items-center gap-space-2">
        {/* 🔴 No `label` prop — see the note on the completions panel. */}
        <SearchField
          className="min-w-[220px] flex-1"
          value={rawSearch}
          onChange={setRawSearch}
          placeholder="Search by name or email…"
        />
        <SortSelect
          value={sort}
          onChange={(next) => {
            setSort(next as PrequalSort);
            setPage(1);
          }}
          options={SORT_OPTIONS}
        />
      </div>

      {/* A filter on this list, not a fourth tab: it narrows one population
          rather than switching to a different one. */}
      <div className="mb-space-3 flex flex-wrap items-center gap-space-3">
        <div
          role="group"
          aria-label="Draft scope"
          className="inline-flex shrink-0 rounded-md border border-border-light bg-white-pure overflow-hidden"
        >
          <ScopeButton current={scope} value="Abandoned" onSelect={setScope}>
            Abandoned
          </ScopeButton>
          <ScopeButton current={scope} value="All" onSelect={setScope}>
            All drafts
          </ScopeButton>
        </div>
        {drafts.data && (
          <span className="text-caption text-gray-mid">
            {drafts.data.totalCount} {scope === "Abandoned" ? "abandoned" : "in progress"}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={drafts.data?.data ?? []}
        loading={drafts.isLoading}
        error={drafts.isError ? (drafts.error as Error).message : null}
        emptyMessage={
          q
            ? "No applications match your search. Search looks at the applicant's name and email address — reference numbers aren't searchable."
            : scope === "Abandoned"
              ? "No applications on this site have gone stale."
              : "No applications are in progress on this site."
        }
        rowKey={(d) => d.draftId}
      />

      {drafts.data && drafts.data.totalCount > 0 && (
        <Pagination
          page={drafts.data.page}
          pageSize={drafts.data.pageSize}
          totalCount={drafts.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </section>
  );
}

function ScopeButton({
  current,
  value,
  onSelect,
  children,
}: {
  current: PrequalificationDraftScope;
  value: PrequalificationDraftScope;
  onSelect: (v: PrequalificationDraftScope) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={active}
      className={cn(
        "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
        active
          ? "bg-gold text-onyx hover:bg-gold-highlight"
          : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10",
      )}
    >
      {children}
    </button>
  );
}
